// based on: https://github.com/ifndefdeadmau5/react-floating-button-menu

import React from 'react';
import styled from 'styled-components';
import { spacing } from 'ui/theme/utils';

import FloatingMenu, { Directions } from './FloatingMenu';
import ChildButton from './ChildButton';
import MainButton from './MainButton';
import { useChildrenWithProps } from 'hooks';

const FloatingMenuButton = ({
  children,
  iconResting,
  iconActive,
  slideSpeed = 300,
  closeOnChildClick = true,
  ...rest
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [zIndex, setZIndex] = React.useState('auto');

  const childrenWithProps = useChildrenWithProps(children, (child) => ({
    onClick: (event) => {
      if (closeOnChildClick) {
        setIsOpen(false);
        updateZIndex(false);
      }
      const { onClick } = child.props;
      if (onClick) {
        onClick(event);
      }
    },
  }));

  const handleClick = () => {
    const open = !isOpen;
    setIsOpen(open);
    updateZIndex(open);
  };

  const updateZIndex = (isOpen) => {
    if (isOpen) {
      setZIndex(1);
    } else {
      setTimeout(() => setZIndex('auto'), slideSpeed);
    }
  };

  return (
    <FloatingMenu
      slideSpeed={slideSpeed}
      direction={Directions.Up}
      alignItems="flex-end"
      spacing={8}
      isOpen={isOpen}
      zIndex={zIndex}
      {...rest}
    >
      <StyledMainButton iconResting={iconResting} iconActive={iconActive} onClick={handleClick} />
      {childrenWithProps}
    </FloatingMenu>
  );
};

const DefaultMainButton = ({ iconResting, iconActive, ...rest }) => {
  return (
    <MainButton
      iconResting={iconResting || <img src="/assets/img/icons/plus-white.svg" alt="more" />}
      iconActive={
        iconActive || (
          <img src="/assets/img/icons/plus-white.svg" className="rotate-45" alt="close" />
        )
      }
      {...rest}
    />
  );
};

const StyledMainButton = styled(DefaultMainButton).attrs(({ size = 50, boxShadow = 'none' }) => ({
  size,
  boxShadow,
}))`
  border-radius: 50%;
  background-color: ${({ theme, bg = 'primary' }) => theme.colors[bg] || bg};
  filter: drop-shadow(0px 3px 10px rgba(0, 0, 0, 0.16));
  img {
    width: 20px;
    &.rotate-45 {
      transform: rotate(-45deg);
    }
  }
`;

const StyledChildButton = styled(ChildButton).attrs(
  ({ width = 'auto', height = 38, background = 'white', px = 'm' }) => ({
    width,
    height,
    background,
    px,
  })
)`
  min-width: ${({ minWidth = '140px' }) => minWidth};
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0px 3px 26px rgba(0, 0, 0, 0.16);
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0.04em;
  text-align: center;
  color: ${({ color, theme }) => color || theme.colors.primary};
  ${(props) => spacing(props)}
`;

export {
  FloatingMenuButton,
  StyledChildButton as FloatingMenuChildButton,
  Directions as FloatingMenuDirections,
};
